import { useEffect, useState } from "react";
import { Header } from "../components/Header";
import api from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Pencil, Check, X, Trash2, UserPlus, Database, Activity } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("users");
  const isAdmin = user?.role === "admin";

  return (
    <>
      <Header title="Settings" subtitle="Platform Administration" />
      <div className="p-6 md:p-8 max-w-[1400px] mx-auto space-y-5 min-w-0">
        <div className="flex gap-2 border-b border-slate-200">
          {[
            { k: "users", label: "User Management", admin: true },
            { k: "projects", label: "Active Projects" },
            { k: "sync", label: "Data Sync" },
            { k: "info", label: "Platform" },
          ].map((t) => (
            t.admin && !isAdmin ? null : (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`px-4 py-2 text-xs uppercase tracking-wider font-mono border-b-2 transition-colors ${
                  tab === t.k ? "border-vexen-gold text-slate-900 font-semibold" : "border-transparent text-slate-500 hover:text-slate-900"
                }`}
                data-testid={`settings-tab-${t.k}`}
              >
                {t.label}
              </button>
            )
          ))}
        </div>

        {tab === "users" && isAdmin && <UsersTab />}
        {tab === "projects" && <ProjectsTab />}
        {tab === "sync" && <SyncTab />}
        {tab === "info" && <InfoTab />}
      </div>
    </>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "viewer", password: "" });
  const [reset, setReset] = useState({});

  const load = () => api.get("/users").then((r) => setUsers(r.data.users));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User created");
      setForm({ email: "", name: "", role: "viewer", password: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete user?")) return;
    await api.delete(`/users/${id}`);
    toast.success("Deleted");
    load();
  };

  const resetPwd = async (id) => {
    const np = reset[id];
    if (!np) return;
    try {
      await api.post("/users/reset-password", { user_id: id, new_password: np });
      toast.success("Password reset");
      setReset({ ...reset, [id]: "" });
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={add} className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Email</label>
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required type="email" className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs mt-1" data-testid="new-user-email" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Name</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs mt-1" data-testid="new-user-name" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Role</label>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs mt-1" data-testid="new-user-role">
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="viewer">Viewer</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Password</label>
          <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required type="password" className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs mt-1" data-testid="new-user-password" />
        </div>
        <button className="bg-vexen-sidebar text-white px-3 py-2 rounded text-xs font-semibold flex items-center gap-1 justify-center" data-testid="add-user-button">
          <UserPlus size={13} /> Add User
        </button>
      </form>

      <div className="bg-white border border-slate-200 rounded-md">
        <table className="w-full text-xs" data-testid="users-table">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Email</th>
              <th className="text-left px-4 py-2">Role</th>
              <th className="text-left px-4 py-2">Reset Password</th>
              <th className="text-right px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100">
                <td className="px-4 py-2">{u.name}</td>
                <td className="px-4 py-2 font-mono">{u.email}</td>
                <td className="px-4 py-2 capitalize">{u.role}</td>
                <td className="px-4 py-2">
                  <div className="flex gap-1 items-center">
                    <input
                      type="password"
                      placeholder="New password"
                      value={reset[u.id] || ""}
                      onChange={(e) => setReset({ ...reset, [u.id]: e.target.value })}
                      className="px-2 py-1 border border-slate-200 rounded text-xs"
                    />
                    <button onClick={() => resetPwd(u.id)} className="text-xs text-emerald-600 hover:underline">Reset</button>
                  </div>
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => del(u.id)} className="text-red-600 hover:text-red-800" data-testid={`delete-user-${u.id}`}>
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProjectsTab() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "manager";
  const [projects, setProjects] = useState([]);
  const [edit, setEdit] = useState({});
  const [draft, setDraft] = useState({});

  const load = () => api.get("/projects/all").then((r) => setProjects(r.data.projects));
  useEffect(() => { load(); }, []);

  const toggle = async (p) => {
    await api.post("/projects/config", { project_name: p.project_name, active: !p.active });
    load();
  };
  const saveAlias = async (p) => {
    await api.post("/projects/alias", { project_name: p.project_name, alias: draft[p.project_name] || p.alias });
    setEdit({ ...edit, [p.project_name]: false });
    toast.success("Alias saved");
    load();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md">
      <table className="w-full text-xs" data-testid="projects-table">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-4 py-2">Project</th>
            <th className="text-left px-4 py-2">Alias</th>
            <th className="text-left px-4 py-2">City</th>
            <th className="text-left px-4 py-2">Type</th>
            <th className="text-left px-4 py-2">Status</th>
            <th className="text-right px-4 py-2">Active</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((p) => (
            <tr key={p.project_name} className="border-t border-slate-100">
              <td className="px-4 py-2 font-medium">{p.project_name}</td>
              <td className="px-4 py-2">
                {edit[p.project_name] ? (
                  <div className="flex items-center gap-1">
                    <input
                      value={draft[p.project_name] ?? p.alias}
                      onChange={(e) => setDraft({ ...draft, [p.project_name]: e.target.value })}
                      className="px-2 py-1 border border-slate-200 rounded text-xs"
                    />
                    <button onClick={() => saveAlias(p)} className="text-emerald-600"><Check size={12} /></button>
                    <button onClick={() => setEdit({ ...edit, [p.project_name]: false })} className="text-red-600"><X size={12} /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span>{p.alias}</span>
                    {canEdit && (
                      <button onClick={() => setEdit({ ...edit, [p.project_name]: true })} className="text-slate-400 hover:text-slate-700" data-testid={`edit-alias-${p.project_name.replace(/\s+/g, "-")}`}>
                        <Pencil size={11} />
                      </button>
                    )}
                  </div>
                )}
              </td>
              <td className="px-4 py-2">{p.city}</td>
              <td className="px-4 py-2">{p.project_type}</td>
              <td className="px-4 py-2">{p.status}</td>
              <td className="px-4 py-2 text-right">
                <button
                  disabled={!canEdit}
                  onClick={() => toggle(p)}
                  className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-mono rounded ${
                    p.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"
                  } ${canEdit ? "hover:opacity-80" : "opacity-60 cursor-not-allowed"}`}
                  data-testid={`toggle-project-${p.project_name.replace(/\s+/g, "-")}`}
                >
                  {p.active ? "On" : "Off"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SyncTab() {
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get("/settings/sync-status").then((r) => setStatus(r.data)); }, []);
  if (!status) return <div className="text-sm">Loading...</div>;

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="text-emerald-600" size={16} />
        <span className="text-sm font-semibold">System Healthy</span>
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Last sync · {new Date(status.last_sync).toLocaleString()}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
        {Object.entries(status.collections).map(([k, v]) => (
          <div key={k} className="bg-slate-50 border border-slate-200 rounded p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{k}</div>
            <div className="font-mono font-bold text-lg mt-1">{v.toLocaleString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InfoTab() {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Platform</div>
        <div className="font-semibold text-base">Vexen · Realty Intelligence</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Brand</div>
        <div>Scitus.ai</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Build</div>
        <div className="font-mono text-xs">v1.0.0 · 2026</div>
      </div>
      <div className="pt-3 border-t border-slate-200 text-xs text-slate-600 leading-relaxed">
        Vexen is a decision intelligence platform built for Indian real estate developers and CXOs.
        Designed for command-center workflows across sales, collections, construction, inventory, and market posture.
      </div>
    </div>
  );
}
